import {isMobile} from '../../common/util/isMobile';
import {MEDIA_TYPE, getMediaType} from '../components/GifConstants';

type MathServiceArgs = {
    element: HTMLElement,
    staticColumns: number,
    showShareCount: boolean,
    isUniversalGifList: boolean,
}

export class MathService<T> {
    element: HTMLElement;
    staticColumns: number;
    showShareCount: boolean;
    isUniversalGifList: boolean;
    DEFAULT_WIDTH: number;
    y: number;
    containerWidth: number;
    numColumns: number;
    i: number;
    colNum: number;
    currentColumnIdx: number;
    minBottomY: number;
    maxBottomY: number;
    columnTailItems: api.PostDetails[];
    spacing: number;
    borderLeft: number;
    borderRight: number;
    paddingLeft: number;
    paddingRight: number;
    paddingTop: number;
    paddingBottom: number;

    constructor({element, staticColumns, showShareCount,
        isUniversalGifList}: MathServiceArgs) {
        this.element = element;
        this.staticColumns = staticColumns;
        this.showShareCount = showShareCount;
        this.isUniversalGifList = isUniversalGifList;
        this.DEFAULT_WIDTH = this.staticColumns === 1 ? 200 : 400;

        this.y = 0;
        this.containerWidth = 0;
        this.numColumns = 0;
        this.i = 0;
        this.colNum = 0;
        this.currentColumnIdx = 0;
        this.minBottomY = 0;
        this.maxBottomY = 0;
        this.columnTailItems = [];
        this.spacing = 10;
        this.borderLeft = 0;
        this.borderRight = 0;
        this.paddingLeft = 0;
        this.paddingRight = 0;
        this.paddingTop = 0;
        this.paddingBottom = 0;
        this.resetVars();
    }
    resetVars() {
        this.y = 0;
        this.spacing = isMobile() ? 10 : 20;
        const style = this.element ?
            window.getComputedStyle(this.element) : null;
        this.paddingLeft = style ?
            parseInt(style.getPropertyValue('padding-left')) : 0;
        this.paddingRight = style ?
            parseInt(style.getPropertyValue('padding-right')) : 0;
        this.paddingTop = style ?
            parseInt(style.getPropertyValue('padding-top')) : 0;
        this.paddingBottom = style ?
            parseInt(style.getPropertyValue('padding-bottom')) : 0;
        this.borderLeft = style ?
            parseInt(style.getPropertyValue('border-left-width')) : 0;
        this.borderRight = style ?
            parseInt(style.getPropertyValue('border-right-width')) : 0;
        this.containerWidth = this.element && this.element.offsetWidth ?
            (this.element.offsetWidth - this.paddingLeft -
                this.paddingRight - this.borderLeft - this.borderRight) :
            this.DEFAULT_WIDTH;

        if (this.staticColumns) {
            this.numColumns = this.staticColumns;
        } else if (this.containerWidth > 1100) {
            this.numColumns = 4;
        } else if (this.containerWidth > 576) {
            this.numColumns = 3;
        } else {
            this.numColumns = 2;
        }
        this.i = 0;
        this.maxBottomY = 0;
        this.columnTailItems = [];
        for (this.colNum = 0; this.colNum < this.numColumns;
            this.colNum++) {
            this.columnTailItems[this.colNum] = {
                bottomY: 0,
                width: 0,
                height: 0,
                y: 0,
                column: this.colNum,
                index: -1,
            };
        }
    }
    /**
     * @abstract
     */
    getItemDimsAndMediaType(item: T): [number, number, string] {
        return [0, 0, ''];
    }
    /**
     * @abstract
     */
    updateItem(item: T, currentColumnIdx: number) {
    }
    compute(data: T[], reset?: boolean): {
        loaderHeight: number, numColumns: number} {
        if (reset) {
            this.resetVars();
        }

        if (!data || data.length === 0) {
            console.info('mathService: no data');
            return {
                loaderHeight: 0,
                numColumns: 0,
            };
        }

        let height = 0;
        const width = (this.containerWidth -
            (this.numColumns * this.spacing)) / this.numColumns;
        let imgWidth: number = 0;
        let imgHeight: number = 0;
        let mediaType: string = '';
        let item;
        // go forwards through array and add missing data
        for (; this.i < data.length; this.i++) {
            item = data[this.i];

            [
                imgWidth,
                imgHeight,
                mediaType,
            ] = this.getItemDimsAndMediaType(item);
            if (!this.isUniversalGifList && (mediaType === MEDIA_TYPE.MEME ||
                    mediaType === MEDIA_TYPE.STICKER)) {
                height = width;
            } else {
                height = width / imgWidth * imgHeight;
            }

            this.currentColumnIdx = 0;
            this.minBottomY = this.columnTailItems[
                this.currentColumnIdx].bottomY;
            for (this.colNum = 1; this.colNum < this.numColumns;
                this.colNum++) {
                // Find the new this.minBottomY.
                if (this.columnTailItems[this.colNum].bottomY <
                    this.minBottomY) {
                    this.currentColumnIdx = this.colNum;
                    this.minBottomY = this.columnTailItems[
                        this.currentColumnIdx].bottomY;
                }
            }

            this.y = this.minBottomY;
            if (this.showShareCount) {
                // HARDCODED
                this.y += 18;
            }

            this.columnTailItems[this.currentColumnIdx] = {
                width,
                height,
                y: this.y,
                bottomY: height + this.y + this.spacing,
                column: this.currentColumnIdx,
                index: this.i,
            };

            this.updateItem(item, this.currentColumnIdx);
        }
        for (this.colNum = 0; this.colNum < this.numColumns; this.colNum++) {
            if (this.columnTailItems[this.colNum].bottomY > this.maxBottomY) {
                this.maxBottomY = this.columnTailItems[this.colNum].bottomY;
            }
        }
        return {
            loaderHeight: this.maxBottomY + this.paddingTop +
                this.paddingBottom,
            numColumns: this.numColumns,
        };
    }
}

export class PostMathService extends MathService<api.Post> {
    getItemDimsAndMediaType(item: api.Post): [number, number, string] {
        if (!item.media_formats.tinygif) {
            console.warn(`missing media`, item);
        }
        return [
            item.media_formats.tinygif.dims[0],
            item.media_formats.tinygif.dims[1],
            getMediaType(item),
        ];
    }
    updateItem(item: api.Post, currentColumnIdx: number) {
        item.details = this.columnTailItems[currentColumnIdx];
    }
}

export class HTMLElementMathService extends MathService<HTMLElement> {
    getItemDimsAndMediaType(item: HTMLElement): [number, number, string] {
        return [
            parseInt(item.dataset.width || '0'),
            parseInt(item.dataset.height || '0'),
            '',
        ];
    }
    updateItem(item: HTMLElement, currentColumnIdx: number) {
        item.dataset.colIndex = currentColumnIdx.toString();
        const details = this.columnTailItems[currentColumnIdx];
        const gif = item.getElementsByTagName('img')[0];
        // Setting the attribute (instead of gif.width or gif.height
        // directly) helps prevent rounding of floats, which allows us to
        // duplicate Inferno's behavior.
        gif.setAttribute('width', details.width.toString());
        gif.setAttribute('height', details.height.toString());
    }
}
